import {FetchBaseQueryError} from '@reduxjs/toolkit/query';
import {VwpErrorResponse} from '../store/generated/api';

export const extractErrorCode = (error: any): string | undefined => {
    // Handle RTK Query errors
    if (error && typeof error === 'object' && 'data' in error) {
        const errorData = (error as FetchBaseQueryError).data as VwpErrorResponse;
        return errorData.error;
    }

    // Handle direct error responses
    if (error && typeof error === 'object') {
        const errorResponse = error as VwpErrorResponse;
        return errorResponse.error;
    }

    // Handle string errors
    if (typeof error === 'string') {
        return error;
    }

    return undefined;
};

export const extractErrorKey = (error: any): string | undefined => {
    // For internal errors with a translation key set
    if (error && typeof error === 'object' && 'key' in error) {
        return error.key;
    }

    return undefined;
};

export const extractErrorMessage = (error: any): string => {
    // Handle RTK Query errors
    if (error && typeof error === 'object' && 'data' in error) {
        const errorData = (error as FetchBaseQueryError).data as VwpErrorResponse;
        return errorData.message || errorData.error || 'Unknown error occurred';
    }

    // Handle direct error responses
    if (error && typeof error === 'object') {
        const errorResponse = error as VwpErrorResponse;
        return errorResponse.message || errorResponse.error || 'Unknown error occurred';
    }

    // Handle string errors
    if (typeof error === 'string') {
        return error;
    }

    return 'Unknown error occurred';
};

export const extractHttpStatusCode = (error: any): number | undefined => {
    // Handle RTK Query errors
    if (error && typeof error === 'object' && 'status' in error) {
        return (error as FetchBaseQueryError).status as number;
    }

    // VwpErrorResponse doesn't have a status field, so we can't extract it from there
    // Status will only be available from RTK Query's FetchBaseQueryError

    return undefined;
};


export const getLocalizedErrorMessage = (
    error: any,
    t: (key: string, options?: any) => string
): string => {
    const errorCode = extractErrorCode(error);
    const errorMessage = extractErrorMessage(error);
    const errorKey = extractErrorKey(error);
    const statusCode = extractHttpStatusCode(error);

    if (errorKey) {
        const localizedMessage = t(errorKey);
        if (localizedMessage !== errorKey) {
            return localizedMessage;
        }
    }

    if (errorCode) {
        const localizedMessage = t(`error.${errorCode}`);
        if (localizedMessage !== `error.${errorCode}`) {
            return localizedMessage;
        }
    }

    if (statusCode) {
        const localizedMessage = t(`error.HTTP_${statusCode}`);
        if (localizedMessage !== `error.HTTP_${statusCode}`) {
            return localizedMessage;
        }
    }

    return t('error.generic', {message: errorMessage});
};

export const extractValidationErrors = (error: any): any | null => {
    if (error && typeof error === 'object' && 'data' in error) {
        const errorData = (error as FetchBaseQueryError).data as VwpErrorResponse;
        return errorData.details?.validationErrors || null;
    }

    if (error && typeof error === 'object') {
        const errorResponse = error as VwpErrorResponse;
        return errorResponse.details?.validationErrors || null;
    }

    return null;
};

export const isNetworkError = (error: any): boolean => {
    if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as FetchBaseQueryError).status;
        return status === 0 || status === undefined;
    }
    return false;
};

export const isAuthError = (error: any): boolean => {
    if (error && typeof error === 'object' && 'status' in error) {
        return (error as FetchBaseQueryError).status === 401;
    }
    return false;
};

export const isValidationError = (error: any): boolean => {
    return extractValidationErrors(error) !== null;
};

export const isServerError = (error: any): boolean => {
    if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as FetchBaseQueryError).status;
        return typeof status === 'number' && status >= 500;
    }
    return false;
};